import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, notification } from 'antd'
import _ from 'lodash'
import classNames from 'classnames'
import LinkPurple from 'components/design/Form/LinkPurple'
import ButtonBlack from 'components/design/Form/ButtonBlack'
import ButtonWhite from 'components/design/Form/ButtonWhite'
import { loginWithProvider } from 'services/user'
import FormInput from '../../design/Form/Input'
import FormCheckbox from '../../design/Form/Checkbox'
import style from '../../../layouts/Auth/style.module.scss'
import signupStyles from './style.module.scss'
import constants from '../../../constants'

const CompanyEmailValidator = require('company-email-validator')
const validator = require('email-validator')

const SignupForm = ({ footer }) => {
  const [form] = Form.useForm()
  const [email, setEmail] = useState('')
  const [nudgeToEnterBusinessEmail, setNudgeToEnterBusinessEmail] = useState(false)
  const [validEmailError, setValidEmailError] = useState(false)
  const user = useSelector(state => state.user)
  const emailInput = useRef(null)
  const dispatch = useDispatch()
  const [loadingProvider, setLoadingProvider] = useState(null)

  useEffect(() => {
    const notifyOpener = () => {
      if (window.opener) {
        console.log('Sending READY message to preview. form')
        window.opener.postMessage({ type: 'READY' }, constants.builderDomain)
      }
    }

    const registerMessageListener = () => {
      window.addEventListener('message', handleMessage)
    }

    notifyOpener()
    registerMessageListener()
  }, [])

  const handleMessage = event => {
    const { origin, data, source } = event
    if (origin === constants.builderDomain) {
      const { type, key, value } = data
      if (type === 'SET') {
        localStorage.setItem(key, JSON.stringify(value))

        // Send success response back to App 1
        source.postMessage({ type: 'SET_SUCCESS', key, value }, origin)
      }
    } else {
      console.warn('Message received from an untrusted origin: form', origin)
    }
  }

  const handleEmailInput = e => {
    setEmail({ email: e.target.value })

    const personalEmailDomains = [
      'gma',
      'gmai',
      'gmail',
      'gmail.',
      'gmail.c',
      'gmail.co',
      'gmail.com',
      'hot',
      'hotm',
      'hotma',
      'hotmai',
      'hotmail',
      'hotmail.c',
      'hotmail.co',
      'hotmail.com',
      'yah',
      'yaho',
      'yahoo',
      'yahoo.',
      'yahoo.c',
      'yahoo.co',
      'yahoo.co.i',
      'yahoo.co.in',
      'yahoo.com',
    ]

    if (email === '') {
      setValidEmailError(false)
    } else if (document.activeElement === emailInput.current.input) {
      setValidEmailError(false)
    } else if (email !== '' && !validator.validate(email)) {
      setValidEmailError(true)
    }
    if (
      e.target.value !== '' &&
      e.target.value.includes('@') &&
      e.target.value.split('@')[1].length >= 3 &&
      personalEmailDomains.includes(e.target.value.split('@')[1])
    ) {
      setNudgeToEnterBusinessEmail(true)
    } else if (
      e.target.value !== '' &&
      e.target.value.includes('@') &&
      e.target.value.split('@')[1].length >= 3 &&
      !personalEmailDomains.includes(e.target.value.split('@')[1])
    ) {
      setNudgeToEnterBusinessEmail(false)
    } else if (CompanyEmailValidator.isCompanyEmail(e.target.value)) {
      setNudgeToEnterBusinessEmail(false)
    } else {
      setNudgeToEnterBusinessEmail(false)
    }
  }

  const onFinish = values => {
    if (validator.validate(values.email)) {
      dispatch({
        type: 'user/REGISTER_EMAIL',
        payload: { ...values },
        onComplete: afterEmailVerificationSent,
      })
    } else {
      setValidEmailError(true)
    }
  }

  const afterEmailVerificationSent = ({ error, cancelled, data }) => {
    if (!error && !cancelled && data.data) {
      if (constants.captureGTMEvents && window.CometChatAnalytics) {
        window.CometChatAnalytics.event(constants.GAEvents.user_awaiting_verification)
      }
    }
  }

  let emailErrorMessage = ''

  if (email !== '' && validEmailError) {
    emailErrorMessage = 'Please enter a valid email'
  } else if (email !== '' && nudgeToEnterBusinessEmail) {
    emailErrorMessage = 'We recommend using your work email for an enhanced onboarding experience'
  }

  const handleProviderLogin = async provider => {
    setLoadingProvider(provider)
    try {
      await loginWithProvider(provider)
    } catch (error) {
      notification.error({
        message: error.response.data.error.message,
        placement: 'bottomRight',
      })
    } finally {
      setLoadingProvider(null)
    }
  }

  return (
    <div className={signupStyles.cometchatSignup}>
      <div className={signupStyles.cometchatSignupContent}>
        <div className={signupStyles.signupFormTitle}>Create an account</div>
        <div className={signupStyles.cometchatSignupActions}>
          <ButtonWhite
            buttonStyles={{ height: '44px' }}
            onClick={() => handleProviderLogin('google')}
            loading={loadingProvider === 'google'}
            disabled={loadingProvider !== null}
            buttonText={
              <div className={signupStyles.cometchatSignupActionItem}>
                <div className={signupStyles.cometchatSignupGoogleIcon} />
                Sign up with Google
              </div>
            }
            buttonType="button"
          />
          <ButtonWhite
            buttonStyles={{ height: '44px' }}
            onClick={() => handleProviderLogin('github')}
            loading={loadingProvider === 'github'}
            disabled={loadingProvider !== null}
            buttonText={
              <div className={signupStyles.cometchatSignupActionItem}>
                <div className={signupStyles.cometchatSignupGithubIcon} />
                Sign up with GitHub
              </div>
            }
            buttonType="button"
          />
        </div>
        <div className={signupStyles.cometchatSignupDivider}>
          <div className={signupStyles.cometchatSignupDividerLine} />
          Or
          <div className={signupStyles.cometchatSignupDividerLine} />
        </div>
        <div className={signupStyles.cometchatSignupForm}>
          <Form
            requiredMark={false}
            form={form}
            colon={false}
            onFinish={onFinish}
            className={classNames(style.formSignUp)}
            initialValues={{
              email:
                _.trim(user.prefilledSignupEmail).length > 0
                  ? _.trim(user.prefilledSignupEmail)
                  : email,
            }}
            style={{ width: '100%' }}
          >
            <FormInput
              onChange={handleEmailInput}
              Form={Form}
              label={<div className={signupStyles.cometchatSignupInputLabel}>Email</div>}
              style={{ height: '44px', borderColor: '#d5d7da' }}
              name="email"
              value={email}
              placeholder="Your work email"
              rules={[{ required: true, message: 'Please enter your email' }]}
              disabled={
                _.trim(user.prefilledSignupEmail).length > 0 && user.prefilledSignupEmailDisabled
              }
              subTextCustom={emailErrorMessage}
              ref={emailInput}
            />

            <div className={style.checkboxInputWrapper} style={{ marginBottom: '40px' }}>
              <FormCheckbox
                Form={Form}
                valuePropName="checked"
                formCheckBoxStyles={{ height: '20px' }}
                customLabelStyles={{ marginTop: '0px' }}
                rules={[
                  {
                    validator: (r, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              'Please agree to the terms and privacy policy to continue.',
                            ),
                          ),
                  },
                ]}
                name="agreeToTerms"
                content={
                  <div className={signupStyles.termsText}>
                    {' '}
                    I agree to CometChat&apos;s{' '}
                    <a
                      href="https://www.cometchat.com/legal-terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      terms
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.cometchat.com/legal-privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      privacy policy
                    </a>
                    .
                  </div>
                }
              />
            </div>
            <ButtonBlack
              buttonText="Create Account"
              buttonType="submit"
              loading={user.loading}
              buttonStyles={{
                height: '44px',
                fontSize: '16px',
              }}
            />
          </Form>
          <div className={signupStyles.cometchatSignupFooter}>
            <div className={signupStyles.cometchatSignupFooterText}>Already have an account? </div>
            <div>
              <LinkPurple to="/login" linktext="Log in" classes={style.logInLink} />
            </div>
          </div>
        </div>
      </div>
      {footer}
    </div>
  )
}

export default SignupForm
